/* global Office */

import { createClient, RoomType } from "matrix-js-sdk";
import { addJitsiWidgetLite } from "../../src/taskpane/utils/addJitsiWidgetLite";

function getRoomPermalink(roomId: string, isExternal: boolean, creds: any): string {
  const permalinkPrefix =
    creds.wellKnown?.["config.client.salax"]?.permalink_prefix || creds.wellKnown?.permalink_prefix;
  let base;
  if (permalinkPrefix) {
    base = `${permalinkPrefix.replace(/\/+$/, "")}/#/${roomId}`;
  } else if (creds.webClientUrl) {
    base = `${creds.webClientUrl.replace(/\/+$/, "")}/#/${roomId}`;
  } else {
    base = `https://salax.ssh.net/#/${roomId}`;
  }
  if (isExternal) {
    const afterHash = base.split("#/")[1] || "";
    if (afterHash.includes("?")) {
      base += "&external=true";
    } else {
      base += "?external=true";
    }
  }
  return base;
}

async function createInternalMeeting(event: Office.AddinCommands.Event) {
  const credsStr = Office.context.roamingSettings.get("salaxCreds");
  let creds: any = null;
  if (credsStr) {
    creds = JSON.parse(credsStr);
  }

  console.log("Creating internal meeting with creds:", creds);

  if (!creds || !creds.accessToken) {
    Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXCredsMissing", {
      type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
      message: "Please click the 'Configure a meeting' button to sign in first",
    });
    event.completed();
    return;
  }

  const item = Office.context.mailbox.item;

  try {
    const client = createClient({
      baseUrl: creds.homeserverUrl,
      accessToken: creds.accessToken,
      userId: creds.userId,
      deviceId: creds.deviceId,
    });

    await client.startClient({ initialSyncLimit: 1 });

    // Step 1: Create room with placeholder name
    const tempRoomName = "SalaX Outlook Meeting";
    const roomOptions = {
      name: tempRoomName,
      preset: "public_chat",
      visibility: "public",
      creation_content: { type: RoomType.ElementVideo },
      power_level_content_override: {
        users: { [creds.userId]: 200 },
      },
    };

    const { room_id } = await client.createRoom(roomOptions);

    // Wait for room to exist
    await new Promise((resolve) => {
      const fromCache = client.getRoom(room_id);
      if (fromCache) return resolve(fromCache);
      const onRoom = (r: any) => {
        if (r.roomId === room_id) {
          client.removeListener("Room", onRoom);
          resolve(r);
        }
      };
      client.on("Room", onRoom);
    });

    // Step 2: Compose final name (e.g., "ABCD - 03.12.25")
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0"); // JS months are 0-based
    const year = String(now.getFullYear()).slice(-2); // Last two digits
    const shortId = room_id.startsWith("!") ? room_id.slice(1, 5) : room_id.slice(0, 4);

    const finalRoomName = `${shortId} - ${day}.${month}.${year}`;

    // Step 3: Set the real room name
    await client.setRoomName(room_id, finalRoomName);

    const jitsiDomain =
      creds.wellKnown?.["im.vector.riot.jitsi"]?.preferredDomain || "call.ssh.sshcollabx.net";


    // Step 4: Use finalRoomName everywhere else
    await addJitsiWidgetLite({
      client,
      roomId: room_id,
      roomName: finalRoomName,
      jitsiDomain,
      useJwt: true,
      isVideoChannel: true,
      ifExternalMeeting: false,
    });

    await client.setPowerLevel(room_id, creds.userId, 100);

    const publicLink = getRoomPermalink(room_id, false, creds);
    const title = `SalaX Meeting – ${finalRoomName}`;

    // ✅ Only set subject if currently empty
    item.subject.getAsync((subResult) => {
      if (subResult.status === Office.AsyncResultStatus.Succeeded) {
        const current = (subResult.value || "").trim();
        if (!current) {
          item.subject.setAsync(title, (setRes) => {
            if (setRes.status !== Office.AsyncResultStatus.Succeeded) {
              console.error("Failed to set subject:", setRes.error);
            }
          });
        }
      } else {
        console.error("Failed to get subject:", subResult.error);
      }
    });

    // --- Set the location field to the join link (overwriting is OK) ---
    item.location.setAsync(publicLink, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Failed to set location:", result.error);
      }
    });

    // --- SalaX footer block to append ---
    const meetingHtml = `
      <br><br><br><br><br>
      <hr style="border: none; border-top: 2px solid #eaeaea; margin: 24px 0;">
      <div style="font-family: 'Segoe UI', Arial, sans-serif;">
        <div style="font-size: 26px; font-weight: 700; margin-bottom: 12px;">SalaX Secure Messaging</div>
        <a href="${publicLink}"
           target="_blank"
           style="
              color: #0078d4;
              text-decoration: underline;
              font-size: 23px;
              font-weight: bold;
              letter-spacing: 0.2px;
              line-height: 2.2;
              display: inline-block;
              margin-bottom: 14px;
           "
        >Join the meeting now</a>
        <br />
        <span>Video Room Name: ${finalRoomName}</span>
      </div>
    `;

    // ✅ Append SalaX block after existing body HTML (do not wipe)
    item.body.getAsync(Office.CoercionType.Html, (getResult) => {
      let existingHtml = "";
      if (getResult.status === Office.AsyncResultStatus.Succeeded) {
        existingHtml = getResult.value || "";
      } else {
        console.error("Failed to read existing body:", getResult.error);
      }

      const combinedHtml = (existingHtml || "").trim() ? existingHtml + meetingHtml : meetingHtml;

      item.body.setAsync(
        combinedHtml,
        { coercionType: Office.CoercionType.Html },
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
            item.notificationMessages.replaceAsync("SalaXMeetingSuccess", {
              type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
              message: "SalaX meeting link added.",
              icon: "Icon.80x80",
              persistent: false,
            });
          } else {
            item.notificationMessages.replaceAsync("SalaXMeetingError", {
              type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
              message: "Failed to insert SalaX meeting link.",
            });
          }
          try {
            client.stopClient();
          } catch {}
          event.completed();
        }
      );
    });
  } catch (e: any) {
    Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXMeetingError", {
      type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
      message: "SalaX meeting failed: " + e?.toString(),
    });
    event.completed();
  }
}

async function createExternalMeeting(event: Office.AddinCommands.Event) {
  const credsStr = Office.context.roamingSettings.get("salaxCreds");
  let creds: any = null;
  if (credsStr) {
    creds = JSON.parse(credsStr);
  }

  console.log("Creating external meeting with creds:", creds);

  if (!creds || !creds.accessToken) {
    Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXCredsMissing", {
      type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
      message: "Please click the SalaX 'Settings' button to sign in before creating a meeting.",
    });
    event.completed();
    return;
  }

  const item = Office.context.mailbox.item;

  try {
    const client = createClient({
      baseUrl: creds.homeserverUrl,
      accessToken: creds.accessToken,
      userId: creds.userId,
      deviceId: creds.deviceId,
    });

    await client.startClient({ initialSyncLimit: 1 });

    const roomName = "SalaX External Meeting " + new Date().toLocaleString();
    const roomOptions = {
      name: roomName,
      preset: "private_chat",
      visibility: "private",
      creation_content: { type: RoomType.ExternalVideoCall },
      power_level_content_override: {
        users: { [creds.userId]: 200 },
      },
    };

    const { room_id } = await client.createRoom(roomOptions);

    await new Promise((resolve) => {
      const fromCache = client.getRoom(room_id);
      if (fromCache) return resolve(fromCache);
      const onRoom = (r: any) => {
        if (r.roomId === room_id) {
          client.removeListener("Room", onRoom);
          resolve(r);
        }
      };
      client.on("Room", onRoom);
    });

     const jitsiDomain =
      creds.wellKnown?.["im.vector.riot.jitsi"]?.preferredDomain || "call.ssh.sshcollabx.net";

    await addJitsiWidgetLite({
      client,
      roomId: room_id,
      roomName: roomName,
      jitsiDomain,
      useJwt: true,
      isVideoChannel: true,
      ifExternalMeeting: true,
    });

    await client.setPowerLevel(room_id, creds.userId, 100);

    const publicLink = getRoomPermalink(room_id, true, creds); // ?external=true
    const organizerLink = getRoomPermalink(room_id, false, creds); // no external param

    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = String(now.getFullYear()).slice(-2);
    const shortId = room_id.startsWith("!") ? room_id.slice(1, 5) : room_id.slice(0, 4);

    const finalRoomName = `${shortId} - ${day}.${month}.${year}`;
    const meetingTitle = `SalaX Meeting – ${finalRoomName}`;

    // ✅ Only set subject if currently empty
    item.subject.getAsync((subResult) => {
      if (subResult.status === Office.AsyncResultStatus.Succeeded) {
        const current = (subResult.value || "").trim();
        if (!current) {
          item.subject.setAsync(meetingTitle, (setRes) => {
            if (setRes.status !== Office.AsyncResultStatus.Succeeded) {
              console.error("Failed to set subject:", setRes.error);
            }
          });
        }
      } else {
        console.error("Failed to get subject:", subResult.error);
      }
    });

    // --- Set the location field to the public join link ---
    item.location.setAsync(publicLink, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Failed to set location:", result.error);
      }
    });

    // --- SalaX footer block ---
    const meetingHtml = `
      <br><br><br><br><br>
      <hr style="border: none; border-top: 2px solid #eaeaea; margin: 24px 0;">
      <div style="font-family: 'Segoe UI', Arial, sans-serif;">
        <div style="font-size: 26px; font-weight: 700; margin-bottom: 12px;">SalaX Secure Messaging</div>
        <a href="${publicLink}"
           target="_blank"
           style="
              color: #0078d4;
              text-decoration: underline;
              font-size: 23px;
              font-weight: bold;
              letter-spacing: 0.2px;
              line-height: 2.2;
              display: inline-block;
              margin-bottom: 7px;
           "
        >Join the meeting now</a>
    
        <div style="font-size:15px;">
          for meeting organizers: 
          <a href="${organizerLink}" target="_blank" style="color:#0078d4; text-decoration:underline;">start the meeting</a>
        </div>

        <br />
        <span>Video Room Name: ${finalRoomName}</span>
      </div>
    `;

    // ✅ Append SalaX block to existing body instead of replacing
    item.body.getAsync(Office.CoercionType.Html, (getResult) => {
      let existingHtml = "";
      if (getResult.status === Office.AsyncResultStatus.Succeeded) {
        existingHtml = getResult.value || "";
      } else {
        console.error("Failed to read existing body:", getResult.error);
      }

      const combinedHtml = (existingHtml || "").trim() ? existingHtml + meetingHtml : meetingHtml;

      item.body.setAsync(
        combinedHtml,
        { coercionType: Office.CoercionType.Html },
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
            item.notificationMessages.replaceAsync("SalaXMeetingSuccess", {
              type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
              message: "SalaX external meeting link added.",
              icon: "Icon.80x80",
              persistent: false,
            });
          } else {
            item.notificationMessages.replaceAsync("SalaXMeetingError", {
              type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
              message: "Failed to insert SalaX external meeting link.",
            });
          }
          try {
            client.stopClient();
          } catch {}
          event.completed();
        }
      );
    });
  } catch (e: any) {
    Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXMeetingError", {
      type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
      message: "SalaX external meeting failed: " + e?.toString(),
    });
    event.completed();
  }
}

Office.actions.associate("createInternalMeeting", createInternalMeeting);
Office.actions.associate("createExternalMeeting", createExternalMeeting);

Office.onReady(() => {
  // Office.js is ready if you need it here.
});

function action(event: Office.AddinCommands.Event) {
  const message: Office.NotificationMessageDetails = {
    type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
    message: "Performed action.",
    icon: "Icon.80x80",
    persistent: true,
  };

  Office.context.mailbox.item?.notificationMessages.replaceAsync(
    "ActionPerformanceNotification",
    message
  );

  event.completed();
}

Office.actions.associate("action", action);
